import { datadogRum } from '@datadog/browser-rum'

import { DD_RUM_ENABLED_FLAG } from '@src/config/env'
import { LOCALE_COOKIE, REGION_ID_COOKIE } from '@src/config/localisation/locale-config.static-constants'
import { REGION_ID_HEADER, REGION_LANG_HEADER } from '@src/constants/api.headers.constants'
import { getSafeLocationHref } from '@src/telemetry/integrations/provider-utils/safe-location-utils'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { generateUniqueEventId, trackInternalEvent } from '@src/telemetry/track'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import getCookie from '../../utils/getCookie'
import { getPageContextDetails } from '../../utils/getPageContextDetails'
import { isHttpClientError } from '../../utils/HttpClient'
import isBrowser from '../../utils/isBrowser'

/**
 * Cart-service rejects `GET /carts/{cartId}` with HTTP 422 "Region ID mismatch: X != Y" when the
 * request's region (sent via the cookie-derived `x-gctx-request-origin-region-id` header) does not
 * match the region the cart was created under. It is concentrated on Instagram/Facebook in-app
 * browsers (~75% of occurrences) and on the 102↔101 pair. The cart-service APM span only records
 * the region it received; it cannot see which client source diverged. This diagnostic captures the
 * four region/locale sources the client knows about — cookie, localStorage, the server-rendered
 * locale held in memory, and the header actually sent — so the divergence is attributable.
 * See GDoc "Region ID mismatch Error | Action Plan".
 */
const REGION_MISMATCH_MESSAGE_FRAGMENT = 'region id mismatch'

interface NextDataLike {
  props?: { pageProps?: { _nextI18Next?: { initialLocale?: string } } }
}

const getHttpStatus = (error: unknown): number | null =>
  isHttpClientError(error) ? (error.response?.status ?? null) : null

/** The locale the Next.js server resolved for this request, held in memory — not the cookie. */
const getServerLocaleInMemory = (): string | null => {
  if (!isBrowser()) return null
  try {
    const nextData = (window as unknown as { __NEXT_DATA__?: NextDataLike }).__NEXT_DATA__
    return nextData?.props?.pageProps?._nextI18Next?.initialLocale ?? null
  } catch {
    return null
  }
}

/** Reads a header off the failed request's config (set by the BaseHttpClient localisation interceptor). */
const readSentHeader = (error: unknown, headerKey: string): string | null => {
  if (!isHttpClientError(error)) return null
  const headers = (error.config?.headers ?? {}) as Record<string, unknown>
  const value = headers[headerKey] ?? headers[headerKey.toLowerCase()]
  return value == null ? null : String(value)
}

/** The cart-service error body message, or null when the 422 body carries no specific message. */
const getRawErrorMessage = (error: unknown): string | null => {
  if (!isHttpClientError(error)) return null
  const data = error.response?.data as { description?: string; message?: string } | undefined
  return data?.description ?? data?.message ?? null
}

/**
 * True for the cart-service 422 region-mismatch rejection. `GET /carts/{cartId}` 422s are ~99%
 * region mismatch (Datadog), so we fire when the body confirms it AND when the body carries no
 * specific message (some environments return a generic 422). A 422 with a *different* specific
 * message (e.g. variant lookup, invalid cartId) is excluded.
 */
export const isRegionIdMismatchError = (error: unknown): boolean => {
  if (getHttpStatus(error) !== 422) return false
  const rawMessage = getRawErrorMessage(error)
  if (rawMessage == null) return true
  return rawMessage.toLowerCase().includes(REGION_MISMATCH_MESSAGE_FRAGMENT)
}

/**
 * Fire-and-forget diagnostic for the cart "Region ID mismatch" 422. No-op for any other error, on
 * the server, and whenever telemetry itself throws — it must never affect the cart flow it observes.
 * Routes to internal analytics and, when RUM is enabled, to Datadog RUM (`ecomm-ssr-rum`).
 */
export const reportCartRegionIdMismatch = (error: unknown, { cartId }: { cartId?: string | null } = {}): void => {
  try {
    if (!isBrowser()) return
    if (!isRegionIdMismatchError(error)) return

    const lsData = getLocalisationDataFromStorage()
    const payload = {
      eventId: generateUniqueEventId(),
      cart_id: cartId ?? null,
      error_status: getHttpStatus(error),
      error_message: getRawErrorMessage(error),
      region_id_cookie: getCookie(REGION_ID_COOKIE) ?? null,
      locale_cookie: getCookie(LOCALE_COOKIE) ?? null,
      region_id_local_storage: lsData?.regionId ?? null,
      locale_local_storage: lsData?.locale ?? null,
      server_locale_in_memory: getServerLocaleInMemory(),
      header_region_id: readSentHeader(error, REGION_ID_HEADER),
      header_locale: readSentHeader(error, REGION_LANG_HEADER),
      page_path: getPageContextDetails().currentPage.page_path ?? null,
      page_url: getSafeLocationHref(),
    }

    trackInternalEvent(TELEMETRY_EVENT_NAMES.CART_REGION_ID_MISMATCH, payload)

    if (DD_RUM_ENABLED_FLAG) {
      datadogRum.addError(error, { issue: 'cart_region_id_mismatch', ...payload })
    }
  } catch {
    // Diagnostic telemetry must never break the cart flow it observes.
  }
}
